import React, { useEffect } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import { reportToSegment, types } from '@smartcar/morse';
import { Box, Divider, InputLabel, Tooltip, Typography, useTheme } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { InputField, Select } from '../../../../../../../../components';
import staticText from '../../../../../../../../localization/Application/webhooks';
import { DetailsContainer, FormControl } from './styles';
import { interpolateOptionsWithVehicleCount } from '../util';

const WebhookDetails = ({
  formValues,
  updateWebhookFormValues,
  setNextEnabled,
  handleInputBlur,
  erroredFields,
  liveVehicleCount,
}) => {
  const theme = useTheme();
  const content = staticText.addModal.stepperSections[2];

  const handleInputChange = (event) => {
    const { value, name } = event.target;
    updateWebhookFormValues({ key: name, value });
  };

  useEffect(() => {
    setNextEnabled(
      Boolean(formValues.name) &&
      Boolean(formValues.callbackUri) &&
      Boolean(formValues.units) &&
      Boolean(formValues.autoSubscribe) &&
      Object.keys(erroredFields).length === 0);
  }, [
    formValues.name,
    formValues.callbackUri,
    formValues.units,
    formValues.autoSubscribe,
    erroredFields,
  ]);

  useEffect(() => {
    reportToSegment(types.PAGE, 'Webhook Form - Details');
  }, []);

  // temporary hide units dropdown until we support imperial units
  const fields = content.fields.filter(field => field.key !== 'units');

  return (
    <DetailsContainer>
      <Typography variant="h1">{content.title}</Typography>
      <Box marginTop={3} width="100%">
        {fields.map((field) => {
          const disabled = field.key === 'rules';
          return (
            <Box key={field.key} marginBottom={3}>
              {field.type && field.type === 'select' && (
                <React.Fragment>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: theme.typography.bold.fontFamily,
                      marginBottom: theme.spacing(1),
                      width: '360px',
                    }}
                  >{field.name}
                  </Typography>
                  {field.description && (
                    <Typography variant="caption" color="text.secondary" component="p" mb={1} width="360px">{field.description}</Typography>
                  )}
                  <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {field.inputLabel && (
                      <InputLabel
                        sx={{ marginRight: 3, marginBottom: '0 !important' }}
                        htmlFor={field.key}
                        shrink
                      >
                        {field.inputLabel}
                      </InputLabel>
                    )}
                    <Select
                      id={field.key}
                      name={field.key}
                      value={disabled ? field.options[0].value : (formValues[field.key] || '')}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      options={field.key === 'autoSubscribe' ? interpolateOptionsWithVehicleCount(field.options, liveVehicleCount) : field.options}
                      disabled={disabled}
                      selectStyles={{
                        width: field.key === 'rules' ? '280px' : '360px',
                        ...(disabled && {
                          backgroundColor: theme.palette.grey[100],
                        }),
                      }}
                      required={field.required}
                      typeVariant="body2"
                      placeholderOptionLabel={field.inputPlaceholder}
                    />
                    {field.tooltip && (
                      <Tooltip title={field.tooltip} arrow placement="right" sx={{ marginLeft: theme.spacing(1) }}>
                        <InfoOutlined
                          htmlColor={theme.palette.text.disabled}
                          fontSize="inherit"
                        />
                      </Tooltip>
                    )}
                  </FormControl>
                </React.Fragment>
              )}
              {field.type && field.type === 'text' && (
                <InputField
                  label={field.name}
                  inputName={field.key}
                  description={field.description}
                  inputType="text"
                  inputValues={formValues[field.key] || ''}
                  inputPlaceholder={field.inputPlaceholder}
                  handleChange={handleInputChange}
                  handleBlur={handleInputBlur}
                  errorMessage={erroredFields[field.key] ? field.errorText : ''}
                  required
                />
              )}
              {field.key === 'rules' && <Divider sx={{ margin: theme.spacing(3, 0) }} />}
            </Box>
          );
        })}
      </Box>
    </DetailsContainer>
  );
};

export default WebhookDetails;

WebhookDetails.propTypes = {
  formValues: shape({
    name: string,
    callbackUri: string,
    errorCallbackUri: string,
    units: string,
  }).isRequired,
  updateWebhookFormValues: func.isRequired,
  handleInputBlur: func.isRequired,
  erroredFields: shape({
    name: bool,
    callbackUri: bool,
    errorCallbackUri: bool,
    units: bool,
  }).isRequired,
  setNextEnabled: func.isRequired,
  liveVehicleCount: number,
};

WebhookDetails.defaultProps = {
  liveVehicleCount: undefined,
};
